// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// useAssistantWindow (T-501) — owns the FloatingAssistant's window chrome:
// open/collapsed state, drag position, and per-user persistence.
//
// Desktop: position + open state persist in localStorage.
// Mobile: always uses a safe anchored layout; position and open state are not
// restored from storage (avoids off-screen panels after dragging on desktop).

import { useCallback, useEffect, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

const STORAGE_KEY = "camp_promethean_assistant_window";

export interface WindowOffset {
  /** Distance from the right viewport edge, in px. */
  right: number;
  /** Distance from the bottom viewport edge, in px. */
  bottom: number;
}

interface PersistedState {
  open: boolean;
  offset: WindowOffset;
}

/** Above the FeedbackWidget FAB (bottom-right at 16px). */
export const ASSISTANT_MOBILE_OFFSET: WindowOffset = { right: 8, bottom: 72 };
export const ASSISTANT_DESKTOP_OFFSET: WindowOffset = { right: 16, bottom: 84 };

const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 560;
const ANCHOR_SIZE = 48;

function clampOffset(offset: WindowOffset, isMobile: boolean): WindowOffset {
  if (typeof window === "undefined") return offset;

  const panelWidth = isMobile
    ? Math.min(PANEL_WIDTH, window.innerWidth - 16)
    : PANEL_WIDTH;
  const panelHeight = isMobile
    ? Math.min(PANEL_HEIGHT, window.innerHeight - 96)
    : PANEL_HEIGHT;
  const anchor = isMobile ? ANCHOR_SIZE : 80;

  const maxRight = Math.max(0, window.innerWidth - (isMobile ? panelWidth : anchor));
  const maxBottom = Math.max(0, window.innerHeight - (isMobile ? panelHeight : anchor));

  return {
    right: Math.min(Math.max(0, offset.right), maxRight),
    bottom: Math.min(Math.max(0, offset.bottom), maxBottom),
  };
}

function loadDesktopPersisted(): PersistedState {
  if (typeof window === "undefined") {
    return { open: false, offset: ASSISTANT_DESKTOP_OFFSET };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { open: false, offset: ASSISTANT_DESKTOP_OFFSET };
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    return {
      open: Boolean(parsed.open),
      offset: clampOffset({ ...ASSISTANT_DESKTOP_OFFSET, ...(parsed.offset ?? {}) }, false),
    };
  } catch {
    return { open: false, offset: ASSISTANT_DESKTOP_OFFSET };
  }
}

export interface UseAssistantWindowResult {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  offset: WindowOffset;
  isDragging: boolean;
  isMobile: boolean;
  /** Attach to the drag handle's onPointerDown. */
  onDragStart: (e: React.PointerEvent) => void;
}

export function useAssistantWindow(): UseAssistantWindowResult {
  const isMobile = useIsMobile();
  const desktopInitial = useRef<PersistedState>(loadDesktopPersisted());
  const wasMobileRef = useRef(isMobile);

  const [open, setOpenState] = useState<boolean>(() =>
    isMobile ? false : desktopInitial.current.open,
  );
  const [offset, setOffset] = useState<WindowOffset>(() =>
    isMobile ? ASSISTANT_MOBILE_OFFSET : desktopInitial.current.offset,
  );
  const [isDragging, setIsDragging] = useState(false);

  const dragRef = useRef<{ startX: number; startY: number; startOffset: WindowOffset } | null>(null);

  // Crossing the mobile breakpoint: reset layout instead of reusing desktop drag coords.
  useEffect(() => {
    if (isMobile === wasMobileRef.current) return;
    wasMobileRef.current = isMobile;

    if (isMobile) {
      setOpenState(false);
      setOffset(ASSISTANT_MOBILE_OFFSET);
      return;
    }

    const desktop = loadDesktopPersisted();
    setOffset(desktop.offset);
  }, [isMobile]);

  // Persist desktop state only.
  useEffect(() => {
    if (isMobile) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ open, offset }));
    } catch {
      /* ignore quota / privacy-mode failures */
    }
  }, [open, offset, isMobile]);

  // Keep the window on-screen across viewport resizes.
  useEffect(() => {
    const onResize = () => setOffset((o) => clampOffset(o, isMobile));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMobile]);

  const setOpen = useCallback((next: boolean) => setOpenState(next), []);
  const toggle = useCallback(() => setOpenState((o) => !o), []);

  const onDragStart = useCallback(
    (e: React.PointerEvent) => {
      if (isMobile) return;

      const target = e.target as HTMLElement;
      if (target.closest("button, a, input, textarea, select")) return;

      e.preventDefault();
      dragRef.current = { startX: e.clientX, startY: e.clientY, startOffset: offset };
      setIsDragging(true);

      const handleMove = (ev: PointerEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const next = clampOffset(
          {
            right: drag.startOffset.right - (ev.clientX - drag.startX),
            bottom: drag.startOffset.bottom - (ev.clientY - drag.startY),
          },
          false,
        );
        setOffset(next);
      };

      const handleUp = () => {
        dragRef.current = null;
        setIsDragging(false);
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
      };

      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
    },
    [isMobile, offset],
  );

  return { open, setOpen, toggle, offset, isDragging, isMobile, onDragStart };
}
